import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ArrowRight, X, TrendingUp } from "lucide-react";

/**
 * Smart quota banner for tenant_admin users.
 * Shows a discreet, dismissible banner when usage reaches 80%+ of any quota
 * (users, prospects, sociétés). Clicks through to /tenant/settings#my-subscription-panel.
 */
export default function QuotaBanner() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = sessionStorage.getItem("blitzz_quota_dismissed");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    if (user?.role !== "tenant_admin") return;
    api.get("/billing/my/quota-alerts").then(r => setData(r.data)).catch(() => {});
  }, [user?.user_id]);

  if (user?.role !== "tenant_admin") return null;
  if (!data?.alerts?.length) return null;
  // Don't show on the settings page itself
  if (location.pathname.startsWith("/tenant/settings")) return null;

  // Pick highest-priority un-dismissed alert
  const active = data.alerts.find(a => !dismissed[a.kind]);
  if (!active) return null;

  const handleDismiss = () => {
    const next = { ...dismissed, [active.kind]: Date.now() };
    setDismissed(next);
    try { sessionStorage.setItem("blitzz_quota_dismissed", JSON.stringify(next)); } catch {}
  };

  const isCritical = active.level === "critical";
  const isSoc = active.kind === "societes";

  const msg = isSoc
    ? `Vous avez ${active.current} société${active.current > 1 ? "s" : ""} active${active.current > 1 ? "s" : ""}${active.extra > 0 ? ` (${active.extra} au-delà du quota inclus - frais mensuel +${active.monthly_overage} EUR)` : " - quota inclus atteint"}.`
    : `Vous avez atteint ${Math.round(active.ratio * 100)}% de votre quota ${active.label.toLowerCase()} (${active.current}/${active.limit}).`;

  return (
    <div className={`sticky top-0 z-40 border-b ${isCritical ? "bg-red-500/10 border-red-500/20" : "bg-[#f7c935]/10 border-[#f7c935]/20"}`} data-testid="quota-banner">
      <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center gap-3">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isCritical ? "bg-red-500/15 text-red-400" : "bg-[#f7c935]/15 text-[#f7c935]"}`}>
          {isCritical ? <AlertTriangle className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-semibold ${isCritical ? "text-red-400" : "text-[#f7c935]"}`}>
            {isCritical ? "Quota dépassé" : "Quota bientôt atteint"}
            {data.offer_name && <span className="text-slate-500 font-normal ml-2">({data.offer_name})</span>}
          </div>
          <div className="text-[11px] text-slate-300 truncate">{msg}</div>
        </div>
        <Link to="/tenant/settings" className={`text-[11px] font-bold px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors ${isCritical ? "bg-red-500 text-white hover:bg-red-600" : "bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825]"}`} data-testid="quota-banner-cta">
          Passer à une offre supérieure <ArrowRight className="w-3 h-3" />
        </Link>
        <button onClick={handleDismiss} className="p-1 rounded hover:bg-white/5 flex-shrink-0" data-testid="quota-banner-dismiss" aria-label="Fermer">
          <X className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>
    </div>
  );
}
